import { PATH } from '@/constants/path';
import { NavigationContext } from '@/modules/Context/NavigationContextProvider';
import FooterItem from '@/modules/Footer/components/FooterItem/FooterItem';
import SocialIcons from '@/modules/Footer/components/SocialIcons';
import BCorpLogo from '@/modules/Footer/imgs/b-corp.png';
import MedrecruitFooterLogo from '@/modules/Footer/imgs/medrecruitFooterLogo.png';
import { getScreenMedia, getSubFooterItems } from '@/modules/Footer/utils/footerHelpers';
import { APP_VERSION } from '@/utils/appVersion';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import React, { useContext } from 'react';

import { useMediaQuery } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';

import styles from './Footer.module.scss';

const Copyright = () => {
  const currentYear = new Date().getFullYear();
  return (
    <div className={styles.copyright}>
      <div>
        <span className={styles.noWrap}>&copy; {currentYear} Medrecruit.</span>{' '}
        <span className={styles.noWrap}>All rights reserved.</span>
      </div>
      <div className={styles.noWrap}>Version {APP_VERSION}</div>
    </div>
  );
};

const FooterItemColumns: React.FC<{ isTablet: boolean; isMobile: boolean }> = ({
  isMobile,
  isTablet,
}) => {
  const cmsNavItems = useContext(NavigationContext);
  const screen = getScreenMedia(cmsNavItems, isMobile, isTablet);
  const lastColumnId = screen.columns[screen.columns.length - 1]?.id;
  return screen.columns.map(col => (
    <Grid
      item
      md={3}
      sm={6}
      xs={12}
      key={col.id}
      className={clsx(styles.menuItem, col.id === lastColumnId && styles.socialColumn)}
    >
      {col.footerItemLinks.map(item => (
        <FooterItem
          key={item.id}
          name={item.name}
          href={item.href}
          passHref={item.passHref ?? false}
          subItems={getSubFooterItems(item.navId, cmsNavItems)}
        />
      ))}
      {col.id === lastColumnId && <SocialIcons />}
    </Grid>
  ));
};

const LogoColumn = () => {
  return (
    <Grid item md={4} xs={12} className={styles.logoOuterContainer}>
      <Grid container className={styles.logoInnerContainer}>
        <Image src={MedrecruitFooterLogo} alt="Medrecruit" width={202} height={53} />
        <Link href={PATH.B_CORP} className={styles.bcorpImageContainer}>
          <Image
            src={BCorpLogo}
            alt="Medrecruit is part of Medenterprises, a Certified B Corp"
            width={185}
            height={74}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </Link>
      </Grid>
    </Grid>
  );
};

const ItemsColumn: React.FC<{ isTablet: boolean; isMobile: boolean }> = ({
  isMobile,
  isTablet,
}) => {
  return (
    <Grid item md={8} xs={12} className={styles.itemsColumn}>
      <Grid container spacing={2} className={styles.itemsRow}>
        <FooterItemColumns isMobile={isMobile} isTablet={isTablet} />
      </Grid>
    </Grid>
  );
};

export const Footer = ({ className }: { className?: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <footer className={clsx(styles.container, className)}>
      <div className={styles.inner}>
        <Grid container spacing={2}>
          <LogoColumn />
          <ItemsColumn isTablet={isTablet} isMobile={isMobile} />
        </Grid>
        <Copyright />
      </div>
    </footer>
  );
};

export default Footer;
